import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Pencil, Plus, Trash2, ShieldAlert, ListChecks, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceRulesAPI } from '../services/api';
import { getMasterCrudAccess } from '../lib/masterData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const AdminAttendanceRulesOnlyPage = ({
  routeBasePath = '/admin/attendance-rules',
  importPath = '/admin/import/attendance_rule',
  dashboardHomePath = '/admin',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await attendanceRulesAPI.getRules();
      setRules(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load attendance rules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    const successMessage = location.state?.attendanceRuleSuccessMessage;
    if (!successMessage) return;
    toast.success(successMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const activeRulesCount = rules.filter((rule) => rule.is_active).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Attendance Rules</h2>
          <p className="text-sm text-muted-foreground">Dedicated page for attendance rule policies and tabular rule management.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(importPath)}>
            Import Rules
          </Button>
          {access.canCreate ? (
            <Button onClick={() => navigate(`${routeBasePath}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(dashboardHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {!loading && rules.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard label="Total Rules" value={rules.length} icon={ListChecks} tone="primary" />
          <StatCard label="Active Rules" value={activeRulesCount} icon={CheckCircle2} tone="success" />
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-foreground">Attendance Rules Table</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <CardSkeleton rows={5} />
          ) : rules.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="No attendance rules found"
              description="Create attendance rule policies to define grace periods, thresholds, and penalties."
              actionLabel={access.canCreate ? 'Create Rule' : undefined}
              onAction={access.canCreate ? () => navigate(`${routeBasePath}/new`) : undefined}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Rule Type</TableHead>
                  <TableHead>Grace</TableHead>
                  <TableHead>Threshold</TableHead>
                  <TableHead>Penalty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[180px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell className="font-medium">{rule.name}</TableCell>
                    <TableCell>{rule.rule_type}</TableCell>
                    <TableCell>{rule.grace_minutes} mins</TableCell>
                    <TableCell>{rule.threshold_count}</TableCell>
                    <TableCell>{`${rule.penalty_type} (${rule.penalty_value})`}</TableCell>
                    <TableCell>
                      <Badge variant={rule.is_active ? 'success' : 'secondary'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {access.canView ? (
                          <Button size="icon" variant="outline" title="View" onClick={() => navigate(`${routeBasePath}/${rule.id}/view`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {access.canEdit ? (
                          <Button size="icon" variant="outline" title="Edit" onClick={() => navigate(`${routeBasePath}/${rule.id}/edit`)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {access.canDelete ? (
                          <Button size="icon" variant="outline" title="Delete" onClick={() => navigate(`${routeBasePath}/${rule.id}/delete`)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAttendanceRulesOnlyPage;
